import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import {
  DeviceIds,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsActiveProperty,
  OperatingStateProperties,
  OtherRoomCommands,
  OtherRoomProperties,
  ProgramCommands,
  ProgramProperties,
  RoomOperatingStateProperties,
  RoomsOperatingStateDemand,
  RoomsOperatingStateReason,
  TemperatureProperty,
} from '@vi-iot/client';

import { FeatureService, InstallationFeaturesStore, isReady } from '@store';

import { NO_VALUE } from '@pipes';
import { GTMService } from '../../../shared/services';
import { InstallationService } from '../../services/installation.service';

@Component({
  selector: 'app-other-room-panel',
  templateUrl: './other-room-panel.component.html',
  styleUrls: ['./other-room-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtherRoomPanelComponent implements OnInit {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly NO_VALUE = NO_VALUE;
  @Input() otherRoom: Feature<OtherRoomProperties, OtherRoomCommands>;
  @Input() otherRoomId: string;
  @Input() isExpanded: boolean;

  @Output() expand: EventEmitter<void> = new EventEmitter<void>();

  operatingState$: Observable<Feature<OperatingStateProperties, never>>;
  isSrcStandalone$: Observable<Feature<IsActiveProperty, never>>;
  geofencing$: Observable<Feature<IsActiveProperty, never>>;
  heating$: Observable<Feature<ProgramProperties, ProgramCommands>>;
  targetTemperature$: Observable<Feature<TemperatureProperty, never>>;
  levelsHeating$: Observable<Feature<TemperatureProperty, never>>;
  levelsCooling$: Observable<Feature<TemperatureProperty, never>>;
  operatingProgramsActive$: Observable<Feature<RoomOperatingStateProperties, never>>;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public installationService: InstallationService,
    private featureService: FeatureService,
    private gtmService: GTMService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.isSrcStandalone$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.standaloneMode,
      DeviceIds.RoomControl
    );

    this.geofencing$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsActiveProperty,
      never
    >(FeatureNames.otherRoomGeofencing(this.otherRoomId), DeviceIds.RoomControl);

    this.operatingState$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      OperatingStateProperties,
      never
    >(FeatureNames.otherRoomOperatingState(this.otherRoomId), DeviceIds.RoomControl);

    this.operatingProgramsActive$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.otherRoomOperatingState(this.otherRoomId),
      DeviceIds.RoomControl
    );

    this.levelsHeating$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      TemperatureProperty,
      never
    >(FeatureNames.otherRoomLevelsHeating(this.otherRoomId), DeviceIds.RoomControl);

    this.levelsCooling$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      TemperatureProperty,
      never
    >(FeatureNames.otherRoomLevelsCooling(this.otherRoomId), DeviceIds.RoomControl);

    this.getTargetRoomTemperature();
    this.initActiveProgramSubscription();
  }

  private initActiveProgramSubscription(): void {
    this.operatingProgramsActive$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => isReady(feature)),
        filter((feature) => !!feature?.properties?.level?.value),
        map((feature) => feature.properties.level.value)
      )
      .subscribe((activeProgram) => {
        const srcOperatingProgram = this.featureService.getSRCOperatingProgramHeating(
          activeProgram
        );
        this.heating$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
          FeatureNames.srcOperatingProgram(this.otherRoomId, srcOperatingProgram, true),
          DeviceIds.RoomControl
        );
      });
  }

  private getTargetRoomTemperature(): void {
    this.operatingState$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        filter((feature) => isReady(feature)),
        map((feature) => {
          const demand = feature.properties.demand.value;
          const reason = feature.properties.reason.value;

          return { demand, reason };
        })
      )
      .subscribe((feature) => {
        if (feature.demand === RoomsOperatingStateDemand.cooling) {
          return (this.targetTemperature$ = this.levelsCooling$);
        } else if (feature.reason !== RoomsOperatingStateReason.standby) {
          return (this.targetTemperature$ = this.levelsHeating$);
        }
      });
  }

  onExpand(): void {
    this.expand.emit();
    this.gtmService.track('onPanelExpand', 'Other room', 'singleRoomControl');
  }

  onCollapse(): void {
    this.gtmService.track('onPanelCollapse', 'Other room', 'singleRoomControl');
  }
}
